import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import Select from '@/components/Select';
import { LESSON_STAGE_OPTIONS_MENU } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';

export interface IParentReportFilters {
  game: string[];
  stage: string[];
}

interface IFilterDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialFilters: IParentReportFilters;
  gameOptions: { value: string; label: string }[];
  onApplyFilters: (filters: IParentReportFilters) => void;
}

const FilterDrawer = ({
  isOpen,
  setIsOpen,
  initialFilters,
  gameOptions,
  onApplyFilters,
}: IFilterDrawerProps) => {
  const { formatText } = useFormatText();

  const [selectedGame, setSelectedGame] = useState<string[]>(
    initialFilters.game
  );
  const [selectedStage, setSelectedStage] = useState<string[]>(
    initialFilters.stage
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedGame(initialFilters.game);
    setSelectedStage(initialFilters.stage);
  }, [initialFilters, isOpen]);

  const handleClearFilters = () => {
    setSelectedGame([]);
    setSelectedStage([]);
    onApplyFilters({ game: [], stage: [] });
  };

  const handleApply = () => {
    onApplyFilters({
      game: selectedGame,
      stage: selectedStage,
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      heading={formatText('parent-reports.filter.title')}
      isHeader
      isFooter
      position="right"
      childClassName="items-start gap-6"
      buttonClassName="justify-between"
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        variant: 'link',
        onClick: handleClearFilters,
      }}
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        className: 'h-10 min-w-22 text-xs px-3',
      }}
    >
      <div className="w-full space-y-5">
        <Select
          name={formatText('parent-reports.filters.game')}
          options={gameOptions}
          selected={selectedGame}
          onChange={(option) => {
            if (Array.isArray(option)) {
              setSelectedGame(option);
            }
          }}
          isMulti
          placeholder={formatText('parent-reports.filters.game-placeholder')}
        />

        <Select
          name={formatText('parent-reports.filters.stage')}
          options={LESSON_STAGE_OPTIONS_MENU}
          selected={selectedStage}
          onChange={(option) => {
            if (Array.isArray(option)) {
              setSelectedStage(option);
            }
          }}
          isMulti
          placeholder={formatText('parent-reports.filters.stage-placeholder')}
          isTextFormat
        />
      </div>
    </CustomModal>
  );
};

export default FilterDrawer;
